import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, RefreshCw, MessageCircle, Star, ArrowRight, Clock, Mail } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { LeadFormModal } from "@/components/LeadGenerationForm/LeadFormModal";
import InlineHubSpotChat from "@/components/InlineHubSpotChat";
import { useIsMobile } from "@/hooks/use-mobile";
import SuggestionDetailModal, { type SuggestionFull } from "@/components/SuggestionDetailModal";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

const loadingSteps = [
  { label: "Analyse de votre brief...", image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75" },
  { label: "Recherche de lieux d'exception...", image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2F2FYH1677762931205.jpg&w=2048&q=75" },
  { label: "Sélection des meilleurs sites...", image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FC9co1677753991331.jpg&w=2048&q=75" },
  { label: "Comparaison des options...", image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FfLhq1696321607168.jpg&w=2048&q=75" },
  { label: "Finalisation des suggestions...", image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FoxHh1673372697492.jpg&w=2048&q=75" },
  { label: "Presque prêt...", image: "https://www.naboo.app/_next/image?url=https%3A%2F%2Fwome-houses-prod.s3.eu-west-3.amazonaws.com%2FMRKt1676885471589.jpeg&w=2048&q=75" },
];

const MAX_POLL_DURATION = 120_000;
const POLL_INTERVAL = 3_000;

interface Suggestion extends SuggestionFull {}

const formatPrice = (suggestion: Suggestion): string => {
  if (!suggestion.price || suggestion.price.total_price === null) {
    return "Prix sur demande";
  }
  const { total_price, currency } = suggestion.price;
  const symbol = (!currency || currency === "EUR" || currency === "€") ? "€" : currency;
  return `${Math.round(total_price).toLocaleString("fr-FR")} ${symbol}`;
};

/** Desktop inline chat sidebar — rendered once, never unmounted */
const DesktopChatSidebar: React.FC = () => (
  <div className="hidden lg:block w-[380px] flex-shrink-0 sticky top-24 self-start max-h-[calc(100vh-96px)]">
    <InlineHubSpotChat />
  </div>
);

/* ---------- Sub-components for each state ---------- */

const LoadingContent: React.FC<{ stepIndex: number }> = ({ stepIndex }) => {
  const progress = ((stepIndex + 1) / loadingSteps.length) * 100;
  return (
    <div className="flex flex-col items-center text-center max-w-md w-full">
      <div className="relative w-full h-64 rounded-2xl overflow-hidden mb-6">
        {loadingSteps.map((step, i) => (
          <img
            key={i}
            src={step.image}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
              i === stepIndex ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 p-4">
          {loadingSteps.map((step, i) => (
            <p
              key={i}
              className={`absolute bottom-4 left-4 right-4 text-sm font-medium text-white transition-opacity duration-500 ${
                i === stepIndex ? "opacity-100" : "opacity-0"
              }`}
            >
              {step.label}
            </p>
          ))}
        </div>
      </div>
      <div className="w-full">
        <div className="h-1 w-full rounded-full bg-secondary overflow-hidden">
          <div
            className="h-full rounded-full bg-cta-green transition-all duration-700 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const ErrorContent: React.FC<{ error: string | null; onRetry: () => void }> = ({ error, onRetry }) => (
  <div className="flex flex-col items-center text-center max-w-md">
    <div className="rounded-full bg-orange-100 p-4 mb-6">
      <RefreshCw className="h-10 w-10 text-orange-600" />
    </div>
    <h2 className="text-2xl font-semibold mb-3">Un problème est survenu</h2>
    <p className="text-muted-foreground mb-6">
      {error || "Impossible de charger les suggestions. Veuillez réessayer."}
    </p>
    <Button
      onClick={onRetry}
      className="bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 rounded-xl px-6"
    >
      Recommencer
    </Button>
  </div>
);

const Suggestions: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const isMobile = useIsMobile();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);

  const [status, setStatus] = useState<string>("loading");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (status !== "loading") return;
    const interval = setInterval(() => {
      setStepIndex((prev) => (prev + 1) % loadingSteps.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [status]);

  useEffect(() => {
    if (suggestions.length > 0 && sessionId) {
      const venueNames = suggestions.map(s => s.name);
      sessionStorage.setItem(`suggested_venues_${sessionId}`, JSON.stringify(venueNames));
    }
  }, [suggestions, sessionId]);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setError("Aucun identifiant de session trouvé.");
      setStatus("failed");
      return;
    }

    const startTime = Date.now();

    const poll = async () => {
      if (Date.now() - startTime > MAX_POLL_DURATION) {
        setError("Le délai d'attente a été dépassé. Veuillez réessayer.");
        setStatus("failed");
        return;
      }

      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/check-suggestions-status?session_id=${sessionId}`
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();

        if (data.status === "completed" && data.suggestions) {
          setSuggestions(data.suggestions);
          setStatus("completed");
          return;
        }

        if (data.status === "failed") {
          setError(data.error || "La recherche a échoué.");
          setStatus("failed");
          return;
        }

        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        console.error("[Suggestions] Poll error:", err);
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      pollStatus();
      return;
    }
    try {
      const cached = sessionStorage.getItem(`suggestions_${sessionId}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSuggestions(parsed);
          setStatus("completed");
          sessionStorage.removeItem(`suggestions_${sessionId}`);
          return;
        }
      }
    } catch (e) {
      console.warn("[Suggestions] Failed to read cached data:", e);
    }
    pollStatus();
  }, [pollStatus, sessionId]);

  const handleStartOver = () => {
    navigate("/");
  };

  // Render main content based on status
  const renderMainContent = () => {
    if (status === "loading") {
      return (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 max-w-5xl w-full">
            <LoadingContent stepIndex={stepIndex} />
            <DesktopChatSidebar />
          </div>
        </div>
      );
    }

    if (status === "failed") {
      return (
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 max-w-5xl w-full">
            <ErrorContent error={error} onRetry={handleStartOver} />
            <DesktopChatSidebar />
          </div>
        </div>
      );
    }

    // Completed
    return (
      <main className="flex-1 px-4 sm:px-6 lg:px-8 pt-24 md:pt-28 pb-12">
        <div className="max-w-7xl mx-auto w-full">
          {suggestions.length > 0 && (
            <div className="relative overflow-hidden rounded-3xl border border-[#C6E278]/40 bg-gradient-to-br from-[#C6E278]/20 via-white to-white p-4 md:p-6 mb-6">
              <div aria-hidden className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-[#C6E278]/30 blur-3xl" />
              <div aria-hidden className="pointer-events-none absolute -bottom-16 -left-12 h-40 w-40 rounded-full bg-[#C6E278]/15 blur-3xl" />
              <div className="relative">
                <div className="relative overflow-hidden inline-flex items-center rounded-md bg-[#C6E278] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-black shadow-sm">
                  <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/70 to-transparent animate-shimmer" />
                  <span className="relative">Vos suggestions personnalisées</span>
                </div>
                <h1 className="mt-3 text-2xl md:text-3xl font-semibold tracking-tight leading-tight text-foreground md:whitespace-nowrap">
                  Un conseiller dédié vous recontacte prochainement
                </h1>
                <p className="mt-2 text-sm md:text-base text-black/70 leading-relaxed md:whitespace-nowrap">
                  Pendant ce temps, explorez les lieux sélectionnés ci-dessous ou échangez en direct sur le chat pour aller plus vite.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-3 py-1.5 text-sm text-foreground shadow-sm">
                    <Mail className="h-4 w-4 text-[#C6E278]" />
                    <span>Réponse sous 24h</span>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-3 py-1.5 text-sm text-foreground shadow-sm">
                    <MessageCircle className="h-4 w-4 text-[#C6E278]" />
                    <span>Chat disponible maintenant</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {suggestions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Aucune suggestion trouvée pour vos critères.</p>
              <Button onClick={handleStartOver} variant="outline" className="mt-4 rounded-xl">
                Recommencer
              </Button>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 min-w-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {suggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => setSelectedSuggestion(suggestion)}
                      className="rounded-2xl overflow-hidden border bg-card shadow-sm hover:shadow-lg transition-shadow flex flex-col cursor-pointer group"
                    >
                      <div className="relative overflow-hidden h-52 md:h-60">
                        <img
                          src={suggestion.image_urls?.[0] || "/placeholder.svg"}
                          alt={suggestion.name}
                          className="w-full h-full object-cover"
                        />
                        <span className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-foreground text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                          {formatPrice(suggestion)}
                        </span>
                        {suggestion.top_naboo && (
                          <span className="absolute top-3 left-3 bg-cta-green text-cta-green-foreground text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-sm">
                            🏅 Top Naboo
                          </span>
                        )}
                      </div>
                      <div className="p-4 flex flex-col gap-1.5">
                        <h3 className="font-semibold text-base truncate">{suggestion.name}</h3>
                        {suggestion.location && (
                          <div className="flex items-center text-muted-foreground text-sm">
                            <MapPin className="h-4 w-4 mr-1.5 flex-shrink-0" />
                            <span className="truncate">{suggestion.location}</span>
                          </div>
                        )}
                        {suggestion.description && (
                          <p className="text-sm text-muted-foreground line-clamp-3 mt-1">
                            {suggestion.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
                          <span className="text-sm font-medium text-foreground/70 group-hover:text-foreground transition-colors">
                            Voir le détail
                          </span>
                          <span className="flex items-center justify-center h-7 w-7 rounded-full bg-cta-green text-cta-green-foreground group-hover:scale-110 transition-transform">
                            <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <DesktopChatSidebar />
            </div>
          )}
        </div>
      </main>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header onOpenForm={() => setIsFormOpen(true)} forceScrolled suggestionsMode />
      {renderMainContent()}
      {/* Mobile floating chat button */}
      {isMobile && status === "completed" && suggestions.length > 0 && (
        <Button
          onClick={() => {
            const widget = window.HubSpotConversations?.widget;
            if (!widget) return;
            try {
              const status = widget.status();
              if (status?.loaded) {
                widget.close();
                setTimeout(() => {
                  widget.open();
                }, 100);
              } else {
                widget.open();
              }
            } catch (_) {
              widget.open();
            }
          }}
          className="fixed bottom-4 right-4 z-50 bg-cta-green text-cta-green-foreground hover:bg-cta-green/90 rounded-xl h-12 text-sm font-semibold shadow-lg px-4"
        >
          <MessageCircle className="h-5 w-5 mr-2" />
          Discuter avec votre conseiller
        </Button>
      )}
      <LeadFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
      <SuggestionDetailModal
        suggestion={selectedSuggestion}
        onClose={() => setSelectedSuggestion(null)}
      />
    </div>
  );
};

export default Suggestions;
